import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Flame, Search } from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import { getThemeColor } from '../../config/appConfig';
import '../../styles/module-shared.css';
import apiService from '../../utils/apiService';
import './instruments-commission.css';

const THEME = getThemeColor('primary');

const toList = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);

const formatMoney = (v) => (v === undefined || v === null ? '-' : `$${Number(v).toFixed(5)}`);

const formatDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const mapPayload = (data) => {
  const levels = toList(data?.levels ?? data?.Levels).map((l) => ({
    id: l.affiliateLevelId ?? l.AffiliateLevelId,
    name: l.levelName ?? l.LevelName,
  }));
  const rows = toList(data?.instruments ?? data?.Instruments).map((r) => ({
    id: r.instrumentId ?? r.InstrumentId,
    symbol: String(r.symbol ?? r.Symbol ?? ''),
    commissions: r.commissions ?? r.Commissions ?? {},
    lastUpdated: r.lastUpdatedDate ?? r.LastUpdatedDate,
    isActive: r.isActive ?? r.IsActive ?? true,
  }));
  return { levels, rows };
};

const PopularInstruments = () => {
  const [levels, setLevels] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  // null = popularity order from API, 'asc' | 'desc' = by instrument name
  const [sortDir, setSortDir] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.getPopularInstruments();
      if (res?.success) {
        const mapped = mapPayload(res.data);
        setLevels(mapped.levels);
        setRows(mapped.rows);
      } else {
        setLevels([]);
        setRows([]);
        setError(res?.error || res?.message || 'Failed to load popular instruments');
      }
    } catch (err) {
      setLevels([]);
      setRows([]);
      setError(err?.message || 'Failed to load popular instruments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = q ? rows.filter((r) => r.symbol.toLowerCase().includes(q)) : rows;
    if (sortDir) {
      list = [...list].sort((a, b) => a.symbol.localeCompare(b.symbol));
      if (sortDir === 'desc') list.reverse();
    }
    return list;
  }, [rows, search, sortDir]);

  const toggleSort = () =>
    setSortDir((prev) => (prev === null ? 'asc' : prev === 'asc' ? 'desc' : null));

  const colSpan = levels.length + 3;

  return (
    <div className="page ic-page">
      <div className="page-inner">
        <ModulePageHeader
          icon={Flame}
          title="Popular Instruments"
          subtitle="Most traded instruments and your commission by account tier"
          iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
        />
        <ModuleAlert message={error} />

        <section className="ic-forex">
          <div className="ic-forex__panel">
            {loading ? (
              <div className="module-card" style={{ border: 0, boxShadow: 'none' }}>
                <AppLoader color={THEME} />
              </div>
            ) : (
              <div className="ic-table-wrap">
                <div className="ic-search">
                  <Search className="w-4 h-4" />
                  <input
                    type="search"
                    placeholder="Search instrument (e.g. EURUSD)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <div className="ic-table-scroll">
                  <table className="ic-table">
                    <thead>
                      <tr>
                        <th>
                          <button
                            type="button"
                            onClick={toggleSort}
                            style={{
                              background: 'none',
                              border: 0,
                              padding: 0,
                              font: 'inherit',
                              color: 'inherit',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            Financial Instrument
                            {sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : null}
                            {sortDir === 'desc' ? <ArrowDown className="w-3 h-3" /> : null}
                          </button>
                        </th>
                        {levels.map((l) => (
                          <th key={l.id}>Commission {l.name} Level</th>
                        ))}
                        <th>Last updated date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.length === 0 ? (
                        <tr>
                          <td colSpan={colSpan} className="ic-table__empty">
                            No instruments found.
                          </td>
                        </tr>
                      ) : (
                        visibleRows.map((row) => (
                          <tr key={row.id}>
                            <td>
                              <span className="ic-pair">{row.symbol}</span>
                            </td>
                            {levels.map((l) => (
                              <td key={l.id}>
                                <strong className="ic-rate">{formatMoney(row.commissions[l.id])}</strong>
                              </td>
                            ))}
                            <td>{formatDate(row.lastUpdated)}</td>
                            <td style={{ color: row.isActive ? '#16a34a' : '#dc2626' }}>
                              {row.isActive ? 'Active' : 'Inactive'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default PopularInstruments;
